import { z } from "zod";
import { aiContextScopeSchema, aiTurnResponseSchema, aiTurnSchema } from "./turn.js";

/**
 * `GET /v1/ai/context` — trạng thái trợ lý v2 cho NGƯỜI đang hỏi (US-AIV2-0031 · @UC0191 · ADR-0009), thay shim 0002-S06.
 *
 * Hai nhánh vét cạn, discriminated theo `aiEnabled` (mobile `AiChatOpening` sealed class, 0024):
 *  - `false` + `reason`: `upgrading` = CHƯA có trang nào chạy được ở môi trường này (production tới khi có trang `live`) — **y hệt shim cũ,
 *    từng bit**, nên app build trước 0024 vẫn vẽ màn "đang nâng cấp" (`@UC0191`); `disabled` = có trang chạy được nhưng cổng năng lực
 *    (env · runtime · cờ · vai · nhóm) tắt hết cho người này ⇒ màn "đã tắt".
 *  - `true` + `first`: lượt đầu (chào + chip `run_page` các trang ĐƯỢC PHÉP — 0 LLM), client vẽ ngay; không có "bật mà trống" (ADR-0009 bất biến 8).
 * `.strict()` cả hai nhánh: payload v1 (`greeting`, `chips` rời) không biểu diễn được.
 */
export const AI_CONTEXT_VERSION = 2 as const;
export const AI_CONTEXT_DISABLED_REASONS = ["upgrading", "disabled"] as const;
export type AiContextDisabledReason = (typeof AI_CONTEXT_DISABLED_REASONS)[number];
/** Giữ tên cũ của shim để chỗ đọc (mobile `ai_chat` nhánh "đang nâng cấp", test) không đổi. */
export const AI_SHIM_REASON_UPGRADING = "upgrading" as const;

export const aiContextDisabledSchema = z
  .object({ aiEnabled: z.literal(false), reason: z.enum(AI_CONTEXT_DISABLED_REASONS), version: z.literal(AI_CONTEXT_VERSION) })
  .strict();
// `aiContextScopeSchema` (chip phạm vi) sống ở `turn.ts` từ review #1027: `POST /turns` trả CÙNG hình — một schema, không bản chép.

export const aiContextEnabledSchema = z
  .object({ aiEnabled: z.literal(true), version: z.literal(AI_CONTEXT_VERSION), first: aiTurnSchema, scope: aiContextScopeSchema.optional() })
  .strict();
export const aiContextResponseSchema = z.discriminatedUnion("aiEnabled", [aiContextDisabledSchema, aiContextEnabledSchema]);

export type AiContextScope = z.infer<typeof aiContextScopeSchema>;
export type AiContextDisabled = z.infer<typeof aiContextDisabledSchema>;
export type AiContextEnabled = z.infer<typeof aiContextEnabledSchema>;
export type AiContextResponse = z.infer<typeof aiContextResponseSchema>;

/**
 * 🔴 US-AIV2-tourguide-0007a (@UC0368 · B1 cho 0007b) — `GET /v1/ai/v2/conversations/{id}/guide`: LÀM MỚI dòng dẫn đường của một hội thoại (app gọi khi
 * quay lại sau ≥ 5 phút / từ một màn `open_screen`). Hình `AiTurnResponse` (app đã parse được):
 *  - hội thoại có phiếu ĐANG MỞ (đỉnh ngăn xếp) ⇒ `turn` = đúng bước hiện tại của phiếu (nút Đồng ý mang `version` MỚI — nút cũ hết hạn);
 *  - không phiếu ⇒ `turn` = dòng dẫn đường hiện tại (một câu + một nút ĐÃ KIỂM) hoặc menu khi không có gì để nói.
 * 🔴 GUIDE-FIX-20261020 S3: không phiếu và 0 CỬA (trợ lý vừa tắt · chưa có trang chạy được) ⇒ ĐÚNG nhánh tắt của `GET /context`
 * (`aiContextDisabledSchema` — một hình, không bản chép) thay vì một lượt menu 0 nút; app vẽ màn "đang tắt" như lời chào làm mới khi chưa có hội thoại.
 * 0 lời gọi LLM · 0 dòng ghi (KHÔNG vào `ai_messages` — câu cũ giữ nguyên trong lịch sử, đúng TẠI LÚC đó). Hội thoại của người khác / đã ẩn / id rác ⇒
 * 404 `AI_CONVERSATION_NOT_FOUND` (không lộ tồn tại). App 0007b quyết vẽ: thay/khoá nút của lượt cuối bằng nút của `turn` này.
 */
export const aiGuideRefreshResponseSchema = z.union([aiTurnResponseSchema, aiContextDisabledSchema]);
export type AiGuideRefreshResponse = z.infer<typeof aiGuideRefreshResponseSchema>;

/**
 * Query của `GET /v1/ai/context`: nhóm hiệu lực (màn nhóm) — vắng = tab toàn cục (closed-set groupless).
 *
 * 🔴 US-AIV2-tourguide-0007b (@UC0368 · review #1230 M1) — `peek=true`: CHỈ NHÌN lời chào hiện tại (app làm mới lời chào khi quay lại app mà
 * chưa có hội thoại). Lời chào thường KHÔNG chỉ đọc: nó ghi mốc "đã xem" của thẻ SỰ KIỆN (vai đổi · STK/cấu hình đổi — `markEventsSeen`),
 * chốt mốc sự kiện các nhóm (`freezeEventAnchors`) và đẩy `users.last_seen_at` (`markSeen`). Lần nhìn có thể bị app BỎ (kết quả về muộn, cuộc
 * mới, mất phản hồi) ⇒ ghi ở đây là tiêu thụ LẶNG một thẻ một-lần mà người dùng chưa thấy. `peek` ⇒ 0 dòng ghi nào trong ba thứ đó; thẻ sự
 * kiện đã nhìn sẽ hiện LẠI ở lời chào thật kế tiếp (QĐ điều phối 05/10: "hiện hai lần > mất").
 * `z.enum(["true","false"])`, KHÔNG `z.coerce.boolean()` (`Boolean("false") === true` — tiền lệ `remove-member`).
 */
export const aiContextQuerySchema = z
  .object({
    groupId: z.string().uuid().optional(),
    peek: z
      .enum(["true", "false"])
      .transform((v) => v === "true")
      .optional(),
  })
  .strict();
export type AiContextQuery = z.infer<typeof aiContextQuerySchema>;
